import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

declare const io: any;

@Injectable()
export class ChatWebSocketWidgetService {
  private socket: any | undefined;
  private messagesMap = new Map<string, any[]>();
  private badgeUpdate$ = new Subject<any>();
  private username = '';
  private connectedChatId?: string;
  chatId = '';

  constructor(){
    console.log('ChatWebSocketWidgetService')
  }


  generateUID(): string {
    return uuidv4();
  }

  getBadgeUpdateObservable() {
    return this.badgeUpdate$.asObservable();
  }

  async connectSocket(chatId: string, user_chat?: string): Promise<void> {
    user_chat = user_chat ?? localStorage.getItem('user_chat') ?? undefined;
    if (!user_chat) {
      const newUID = this.generateUID();
      localStorage.setItem('user_chat', newUID);
      user_chat = newUID;
    }

    if (this.socket && this.connectedChatId === chatId) {
      return;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = undefined;
    }

    this.chatId = chatId;
    this.connectedChatId = chatId;

    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    let socketHost = 'slicksell.click';
    const host = window.location.hostname;
    if (
      host.includes('stage-app.slicksell.com.br') ||
      host.includes('stage.slicksell.link')
    ) {
      socketHost = 'stage.slicksell.click';
    }

    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const isAdmin = localStorage.getItem('ss_is_admin') === '1' ? '1' : '0';
    const userId = localStorage.getItem('user_id') || '';

    return new Promise((resolve, reject) => {
      this.socket = io(`${protocol}${socketHost}`, {
        auth: {
          chatId,
          chat_id: chatId,
          user_chat,
          store_id: storeId,
          storeId,
          is_admin: isAdmin,
          userId
        }
      });

      this.socket.on('connect', () => resolve());
      this.socket.on('connect_error', (err: any) => reject(err));

      this.socket.on('load messages', (messages: any[]) => {
        this.messagesMap.set(chatId, messages);
      });
    });
  }

  joinRoom(chatId: string, username: string, user_chat?: string): void {
    this.username = username;
    this.connectSocket(chatId, user_chat);

    this.socket?.emit(
      'new user',
      { name: username, id: chatId, user_chat },
      () => {}
    );
  }

  sendMessage(chatId: string, message: string, user_chat?: string): void {
    user_chat = user_chat ?? localStorage.getItem('user_chat') ?? undefined;
    if (!this.socket) {
      this.connectSocket(chatId, user_chat);
    }

    if (!this.socket) return;

    const isAdmin = localStorage.getItem('ss_is_admin') === '1';
    const userIdStr = localStorage.getItem('user_id');
    const user_id = isAdmin && userIdStr ? Number(userIdStr) : undefined;

    let username = this.username;
    if (isAdmin) {
      const adminName = localStorage.getItem('user_name');
      if (adminName) {
        username = adminName;
      }
    }

    this.socket.emit('chat message', {
      chatId,
      content: message,
      username,
      user_chat,
      is_admin: isAdmin ? 1 : 0,
      user_id
    });
  }

  receiveMessages(): Observable<any> {
    return new Observable(observer => {
      this.socket?.on('chat message', (message: any) => {
        this.addMessageToChat(message.chatId, message);
        observer.next(message);
      });
    });
  }

  getInitialMessages(chatId: string, opts: { refresh?: boolean } = {}): Observable<any[]> {
    if (!chatId) return new Observable(obs => obs.next([]));
    const refresh = !!opts.refresh;

    let cached = this.messagesMap.get(chatId) || [];

    return new Observable(observer => {
      const localUserIp = localStorage.getItem('user_chat') || '';

      const normalize = (arr: any[]) =>
        (arr || [])
          .map(m => {
            let created = m.created_at || m.time || new Date().toISOString();
            if (typeof created === 'string') {
              created = created.trim();
              if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(created)) {
                created = created.replace(' ', 'T');
              }
              created = created.replace(/\.(\d{3})\d+(?=([Zz]|[+\-]\d{2}:?\d{2})?$)/, '.$1');
              if (!/[Zz]|[+\-]\d{2}:?\d{2}$/.test(created)) {
                created = `${created}Z`;
              }
            }
            let createdDate = new Date(created);
            if (!isNaN(createdDate.getTime()) && createdDate.getTime() - Date.now() > 60_000) {
              createdDate = new Date(createdDate.getTime() - 3 * 60 * 60 * 1000);
            }
            const created_at = isNaN(createdDate.getTime()) ? new Date().toISOString() : createdDate.toISOString();
            const ip = String(m.ip ?? m.user_chat_id ?? m.from_id ?? '');
            const sender =
              m.from ??
              m.from_name ??
              m.sender_name ??
              m.username ??
              m.store_name ??
              '';

            if (!!m?.is_admin && !String(sender).trim()) {
              // eslint-disable-next-line no-console
              console.warn('[chat-widget] admin message without sender name:', {
                id: m?.id,
                from: m?.from,
                from_name: m?.from_name,
                sender_name: m?.sender_name,
                username: m?.username,
                store_name: m?.store_name,
                user_chat_id: m?.user_chat_id,
                from_id: m?.from_id,
                ip: m?.ip,
                created_at: m?.created_at
              });
            }

            return {
              ...m,
              ip,
              created_at,
              text: m.text ?? m.content ?? '',
              from: sender,
              isOwnMessage: ip === localUserIp
            };
          })
          .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));

      const dedupe = (arr: any[]) => {
        const seen = new Set<string>();
        const out: any[] = [];
        for (const m of arr || []) {
          const key =
            m?.id != null
              ? `id:${String(m.id)}`
              : `k:${String(m?.ip ?? '')}|${String(m?.created_at ?? '')}|${String(m?.text ?? '')}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(m);
        }
        return out;
      };

      if (!refresh && cached.length) {
        cached = dedupe(normalize(cached));
        observer.next([...cached]);
      }

      this.socket?.emit('getMessages', chatId);
      this.socket?.off?.('messages');
      this.socket?.on('messages', (payload: any) => {
        const incoming = dedupe(normalize(payload?.messages ?? []));
        const base = refresh ? incoming : [...(cached || []), ...incoming];
        const updated = dedupe(normalize(base));
        this.messagesMap.set(chatId, updated);
        observer.next([...updated]);
        observer.complete();
      });
    });
  }

  receiveInitialMessages(): Observable<any[]> {
    return new Observable(observer => {
      this.socket?.on('messages', (messages: any[]) => observer.next(messages));
    });
  }

  addMessageToChat(chatId: string, message: any): void {
    const messages = this.messagesMap.get(chatId) || [];
    messages.push(message);
    this.messagesMap.set(chatId, messages);
  }

  getNumUsers(chatId: string): Observable<number> {
    this.connectSocket(chatId);
    return new Observable(observer => {
      this.socket?.on('num users', (numUsers: number) => observer.next(numUsers));
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = undefined;
    }
    this.cleanupSocket();
    this.messagesMap.clear();
  }

  private cleanupSocket(): void {
    if (this.socket) {
      this.socket.off('connect');
      this.socket.off('connect_error');
      this.socket.off('disconnect');
      this.socket.off('load messages');
      this.socket.off('chat message');
      this.socket.off('num users');
      this.socket.off('messages');
    }
  }

  answerChat(): Observable<never> {
    return throwError(() => new Error('answerChat is not available in widget build'));
  }

  removeMessage(): Observable<never> {
    return throwError(() => new Error('removeMessage is not available in widget build'));
  }
}
